import { inject } from '@angular/core'
import { CanActivateFn, Router } from '@angular/router'
import { AuthService, GenericError } from '@auth0/auth0-angular'
import { catchError, exhaustMap, filter, map } from 'rxjs'

/**
 * Verhindert das aufrufen einer Route wenn der Benutzer nicht authentifiziert ist.
 * Der Benutzer wird auf die Login-Seite weitergeleitet und die Route wird gespeichert.
 * Sobald der Benutzer sich einlogged wird er auf die gespeicherte Route weitergeleitet.
 * @see LoginComponent
 */
export function canActivateAuthGuardFn(): CanActivateFn {
  return (_, state) => {
    const authService = inject(AuthService)
    const router = inject(Router)

    authService.error$.subscribe((e) => console.error('Auth error', e))
    return authService.isAuthenticated$.pipe(
      map((isAuthenticated) => {
        if (!isAuthenticated) {
          // speichert die Route auf die der Benutzer weitergeleitet werden soll
          return router.createUrlTree(['/login'], { queryParams: { target: state.url } })
        }
        return true
      }),
    )
  }
}

/**
 * Überprüft ob der Benutzer sich erneut einloggen muss.
 * Wenn ja, wird er auf die Login-Seite weitergeleitet.
 * @see AuthService
 * @returns Leeres Observable das den Benutzer auf die Login-Seite weiterleitet.
 */
export function reloginIfRequired$() {
  const auth = inject(AuthService)
  return auth.error$.pipe(
    filter(
      (e) =>
        e instanceof GenericError && (e.error === 'login_required' || e.error === 'invalid_grant' || e.error === 'missing_refresh_token'),
    ),
    exhaustMap(() =>
      auth.getAccessTokenSilently().pipe(
        catchError(() =>
          auth.logout({
            logoutParams: { returnTo: window.location.origin },
          }),
        ),
      ),
    ),
  )
}
